import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, FormControl, InputLabel, Select, MenuItem,
  Container, Card, CardContent, Avatar, Chip, Divider, CircularProgress,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Stack, useMediaQuery, useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChatIcon from "@mui/icons-material/Chat";
import TopBar from "./globalComponents/topbar";
import Footer from "./globalComponents/footer";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

// ── Brand palette ───────────────────────────────────────────────────────────
const NAVY = "#0A2E8A";
const GREEN = "#10682A";
const NAVY_GRADIENT = "linear-gradient(135deg,#0A2E8A,#0090D9)";
const NAVY_TINT = "rgba(10, 46, 138, 0.05)";
const NAVY_BORDER_TINT = "rgba(10, 46, 138, 0.2)";

const STATUS_CONFIG = {
  accepted: { gradient: NAVY_GRADIENT, msg: "Your mentor will reach out soon." },
  pending:  { gradient: "linear-gradient(135deg,#F5A623,#E8920A)", msg: "Mentor has not yet viewed your request." },
  rejected: { gradient: "linear-gradient(135deg,#EF4444,#DC2626)", msg: "Mentor isn't available right now." },
  declined: { gradient: "linear-gradient(135deg,#EF4444,#DC2626)", msg: "Mentor isn't available right now." },
};

const TABS = ["application", "experience", "expertise", "availability"];
const REQUESTS_PER_PAGE = 3;

// ── Request card ──────────────────────────────────────────────────────────────
function RequestCard({ request, onCancel, onMessage }) {
  const cfg = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
  return (
    <Card sx={{
      mb: 2, borderRadius: "14px", border: "1px solid #E8EDF2",
      boxShadow: "none", transition: "all 0.22s",
      "&:hover": { boxShadow: "0 6px 24px rgba(10,46,138,0.10)", borderColor: NAVY_BORDER_TINT, transform: "translateY(-1px)" },
    }}>
      <Box sx={{ height: 4, background: cfg.gradient }} />
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
          <Avatar sx={{ width: 44, height: 44, bgcolor: NAVY, fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
            {request.goal?.charAt(0)?.toUpperCase() || "R"}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0D1117", lineHeight: 1.3,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {request.goal}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#8A9BB0", mt: 0.25 }}>
              Sent {new Date(request.createdAt).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}
            </Typography>
          </Box>
          <Chip label={request.status.toUpperCase()} size="small"
            sx={{ fontSize: 10, fontWeight: 700, color: "#fff", background: cfg.gradient, borderRadius: "8px", px: 0.5, flexShrink: 0 }} />
        </Stack>

        <Stack direction="row" spacing={1} mb={1.5}>
          <Button size="small" variant="outlined" onClick={onCancel}
            sx={{ flex: 1, borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12,
              borderColor: "#EF4444", color: "#EF4444", "&:hover": { bgcolor: "#FEF2F2", borderColor: "#EF4444" } }}>
            Cancel
          </Button>
          <Button size="small" startIcon={<ChatIcon sx={{ fontSize: 14 }} />} onClick={onMessage}
            sx={{ flex: 1, borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12,
              color: "#fff", background: NAVY_GRADIENT, boxShadow: "none",
              "&:hover": { boxShadow: "0 4px 14px rgba(10,46,138,0.3)" } }}>
            Message
          </Button>
        </Stack>

        <Divider sx={{ mb: 1 }} />
        <Typography sx={{ fontSize: 11, color: "#8A9BB0" }}>
          <strong style={{ color: "#6B7A8D" }}>Status: </strong>{cfg.msg}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  const labels = { application: "Application", experience: "Experience", expertise: "Expertise", availability: "Availability" };
  return (
    <Box sx={{ bgcolor: NAVY, borderRadius: "10px", p: { xs: 1, sm: 1.5 }, mb: 3, overflowX: "auto" }}>
      <Stack direction="row" spacing={{ xs: 1.5, sm: 2.5 }} sx={{ minWidth: "max-content" }}>
        {TABS.map((tab) => (
          <Box key={tab} onClick={() => onChange(tab)}
            sx={{
              pb: 0.75, cursor: "pointer",
              borderBottom: active === tab ? "2.5px solid #fff" : "2.5px solid transparent",
            }}
          >
            <Typography sx={{
              fontSize: { xs: 12, sm: 14 }, fontWeight: active === tab ? 700 : 400,
              color: active === tab ? "#fff" : "rgba(255,255,255,0.75)",
              transition: "all 0.2s", whiteSpace: "nowrap",
            }}>
              {labels[tab]}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function MentorConnect() {
  const [mentor, setMentor] = useState(null);
  const [requests, setRequests] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("application");

  // Mobile: show either "requests" panel or "profile" panel
  const [mobileView, setMobileView] = useState("profile");

  // Pagination for request history
  const [requestPage, setRequestPage] = useState(0);

  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Fetch mentor
  useEffect(() => {
    const fetchMentor = async () => {
      try {
        setLoading(true);
        const res = await axios.get("https://backend-global-1vq9.onrender.com/api/mentor/list");
        const found = (res.data.mentors || []).find((m) => m.userId === id);
        if (found) setMentor(found);
        else setError("Mentor not found");
      } catch { setError("Failed to load mentor data"); }
      finally { setLoading(false); }
    };
    fetchMentor();
  }, [id]);

  // Fetch requests
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoadingRequests(true);
        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user?.id || user?._id;
        if (userId) {
          const res = await axios.get(`https://backend-global-1vq9.onrender.com/api/connection-requests/mentee/${userId}`);
          setRequests(res.data.data || []);
        }
      } catch { setError("Failed to fetch connection requests."); }
      finally { setLoadingRequests(false); }
    };
    fetchRequests();
  }, []);

  // Fetch availability
  useEffect(() => {
    if (activeTab !== "availability" || !id) return;
    const fetchAvailability = async () => {
      try {
        setLoadingAvailability(true);
        const res = await axios.get(`https://backend-global-1vq9.onrender.com/api/mentorsAvail/${id}/availability`);
        setAvailability(res.data.data || []);
      } catch { console.error("Error fetching availability"); }
      finally { setLoadingAvailability(false); }
    };
    fetchAvailability();
  }, [id, activeTab]);

  // Reset to first page if requests list shrinks below current page's range
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(requests.length / REQUESTS_PER_PAGE) - 1);
    if (requestPage > maxPage) setRequestPage(maxPage);
  }, [requests, requestPage]);

  // ── Tab content ─────────────────────────────────────────────────────────────
  const renderTabContent = () => {
    switch (activeTab) {
      case "application":
        return (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1}>
              <Typography fontWeight={700} fontSize={15} color="#0D1117">Activity Overview</Typography>
              <Chip label="Pending" size="small" sx={{ bgcolor: "#F5A623", color: "#fff", fontWeight: 700, fontSize: 11 }} />
            </Stack>
            <Stack spacing={2}>
              {[
                "Application acceptance pending",
                "Application viewed",
                `Application submitted — ${new Date().toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}`,
              ].map((step, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: i === 2 ? NAVY : "#C5D0DC", flexShrink: 0 }} />
                  <Typography fontSize={13} color="#3D4B5C">{step}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        );

      case "experience":
        return (
          <Box>
            <Typography fontWeight={700} fontSize={15} color="#0D1117" mb={2}>Professional Experience</Typography>
            <Stack spacing={1.5}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <AccessTimeIcon sx={{ color: NAVY, fontSize: 20 }} />
                  <Box>
                    <Typography fontSize={11} color="#8A9BB0" fontWeight={600}>Years of Experience</Typography>
                    <Typography fontSize={14} fontWeight={700} color="#0D1117">{mentor?.yearsOfExperience || "N/A"} years</Typography>
                  </Box>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px" }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <WorkIcon sx={{ color: NAVY, fontSize: 20, mt: 0.25 }} />
                  <Box>
                    <Typography fontSize={11} color="#8A9BB0" fontWeight={600}>Professional Background</Typography>
                    <Typography fontSize={13} color="#3D4B5C" mt={0.25}>{mentor?.professionalBackground || "N/A"}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Box>
        );

      case "expertise":
        return (
          <Box>
            <Typography fontWeight={700} fontSize={15} color="#0D1117" mb={2}>Areas of Expertise</Typography>
            {mentor?.areasOfExpertise?.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {mentor.areasOfExpertise.map((e, i) => (
                  <Chip key={i} label={e}
                    sx={{ bgcolor: NAVY_TINT, color: NAVY, fontWeight: 700, fontSize: 12, border: `1px solid ${NAVY_BORDER_TINT}` }} />
                ))}
              </Box>
            ) : (
              <Typography fontSize={13} color="#8A9BB0">No expertise areas listed.</Typography>
            )}
          </Box>
        );

      case "availability":
        return (
          <Box>
            <Typography fontWeight={700} fontSize={15} color="#0D1117" mb={2}>Availability Calendar</Typography>
            {loadingAvailability ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} sx={{ color: NAVY }} /></Box>
            ) : availability.length > 0 ? (
              <TableContainer component={Paper} sx={{ borderRadius: "10px", border: "1px solid #E8EDF2", boxShadow: "none", overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: NAVY }}>
                      <TableCell sx={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>Date</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>Time Slots</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availability.map((slot, i) => (
                      <TableRow key={i} sx={{ "&:hover": { bgcolor: "#F6F8FB" } }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: 13, color: "#0D1117", whiteSpace: "nowrap" }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <CalendarTodayIcon sx={{ fontSize: 14, color: NAVY }} />
                            <span>{new Date(slot.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {slot.timeSlots?.length > 0 ? slot.timeSlots.map((t, ti) => (
                              <Chip key={ti} label={`${t.start} – ${t.end}`} size="small"
                                sx={{ bgcolor: NAVY_TINT, color: NAVY, fontWeight: 700, fontSize: 11 }} />
                            )) : <Typography variant="caption" color="text.secondary">No slots</Typography>}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: "10px", textAlign: "center" }}>
                <CalendarTodayIcon sx={{ fontSize: 36, color: "#C5D0DC", mb: 1 }} />
                <Typography fontSize={13} color="#8A9BB0">No availability information available.</Typography>
              </Paper>
            )}
          </Box>
        );
      default: return null;
    }
  };

  // ── Requests panel ───────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(requests.length / REQUESTS_PER_PAGE));
  const paginatedRequests = requests.slice(
    requestPage * REQUESTS_PER_PAGE,
    requestPage * REQUESTS_PER_PAGE + REQUESTS_PER_PAGE
  );

  const RequestsPanel = () => (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid #E8EDF2", p: { xs: 2, sm: 2.5 }, height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography fontWeight={700} fontSize={15} color="#0D1117">My Requests History</Typography>
        {requests.length > 0 && (
          <Typography fontSize={11} color="#8A9BB0">
            Page {requestPage + 1} of {totalPages}
          </Typography>
        )}
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {loadingRequests ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} sx={{ color: NAVY }} /></Box>
      ) : requests.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} py={4}>
          <SendIcon sx={{ fontSize: 44, color: "#C5D0DC", mb: 1 }} />
          <Typography fontSize={13} color="#8A9BB0" fontWeight={600}>No connection requests yet</Typography>
          <Typography fontSize={12} color="#8A9BB0">Send a request to get started</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {paginatedRequests.map((req) => (
              <RequestCard key={req._id} request={req}
                onCancel={() => navigate(-1)}
                onMessage={() => navigate("/chat")} />
            ))}
          </Box>

          {totalPages > 1 && (
            <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center" mt={1} pt={2} sx={{ borderTop: "1px solid #E8EDF2" }}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                onClick={() => setRequestPage((p) => Math.max(0, p - 1))}
                disabled={requestPage === 0}
                sx={{
                  borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12,
                  color: NAVY,
                  "&:hover": { bgcolor: NAVY_TINT },
                  "&.Mui-disabled": { color: "#C5D0DC" },
                }}
              >
                Previous
              </Button>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => setRequestPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={requestPage >= totalPages - 1}
                sx={{
                  borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12,
                  color: NAVY,
                  "&:hover": { bgcolor: NAVY_TINT },
                  "&.Mui-disabled": { color: "#C5D0DC" },
                }}
              >
                Next
              </Button>
            </Stack>
          )}
        </>
      )}
    </Box>
  );

  // ── Mentor profile panel ─────────────────────────────────────────────────────
  const ProfilePanel = () => (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid #E8EDF2", p: { xs: 2, sm: 2.5 }, height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography fontWeight={700} color="#8A9BB0" fontSize={13} mb={1.5} sx={{ textTransform: "uppercase", letterSpacing: 1 }}>
        Mentor Profile
      </Typography>
      <Divider sx={{ mb: 2.5 }} />

      {mentor && (
        <Box flex={1} sx={{ overflowY: "auto" }}>
          {/* Mentor header */}
          <Stack direction="row" spacing={2} alignItems="flex-start" mb={2.5}>
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar sx={{ width: { xs: 60, sm: 72 }, height: { xs: 60, sm: 72 }, bgcolor: NAVY, fontSize: { xs: 22, sm: 28 }, fontWeight: 700 }}>
                {mentor.fullName?.charAt(0)?.toUpperCase() || "M"}
              </Avatar>
              <Chip label="Available" size="small"
                sx={{ position: "absolute", bottom: -8, left: "50%", transform: "translateX(-50%)",
                  bgcolor: GREEN, color: "#fff", fontWeight: 700, fontSize: 9, height: 18,
                  "& .MuiChip-label": { px: 0.75 } }} />
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography fontWeight={800} sx={{ fontSize: { xs: 15, sm: 17 }, color: "#0D1117", lineHeight: 1.3 }}>
                {mentor.fullName || "Mentor Name"}
              </Typography>
              <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: NAVY, fontWeight: 600, mb: 0.5 }}>
                {mentor.professionalBackground || "Professional Background"}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <LocationOnIcon sx={{ fontSize: 13, color: "#8A9BB0" }} />
                <Typography fontSize={12} color="#8A9BB0">{mentor.location || "Location"}</Typography>
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2.5 }} />

          {/* Tab bar */}
          <TabBar active={activeTab} onChange={setActiveTab} />

          {/* Tab content */}
          <Box mb={3}>{renderTabContent()}</Box>

          {/* Action buttons */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button variant="outlined" fullWidth onClick={() => navigate(-1)}
              sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 13,
                borderColor: "#E8EDF2", color: "#6B7A8D", "&:hover": { borderColor: "#8A9BB0" } }}>
              ← Go Back
            </Button>
            <Button variant="contained" fullWidth startIcon={<SendIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate(`/connectionReq/${id}`)}
              sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, fontSize: 13,
                background: NAVY_GRADIENT, boxShadow: "none",
                "&:hover": { boxShadow: "0 4px 14px rgba(10,46,138,0.3)" } }}>
              Send Connection Request
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#F3F6FA" }}>
        <TopBar />
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress size={48} sx={{ color: NAVY }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#F3F6FA" }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 4, lg: 8 }, py: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography fontWeight={800} color="#0D1117" sx={{ fontSize: { xs: 20, md: 26 }, lineHeight: 1.2 }}>
              Connect with Mentors
            </Typography>
            <Typography fontSize={13} color="#8A9BB0" mt={0.5}>
              Review mentor profiles and send connection requests
            </Typography>
          </Box>

          {/* Mobile toggle */}
          {isMobile && (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant={mobileView === "profile" ? "contained" : "outlined"}
                onClick={() => setMobileView("profile")}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12,
                  ...(mobileView === "profile" ? { bgcolor: NAVY, "&:hover": { bgcolor: "#082460" }, boxShadow: "none" } : { borderColor: NAVY, color: NAVY }) }}>
                Profile
              </Button>
              <Button size="small" variant={mobileView === "requests" ? "contained" : "outlined"}
                onClick={() => setMobileView("requests")}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, fontSize: 12,
                  ...(mobileView === "requests" ? { bgcolor: NAVY, "&:hover": { bgcolor: "#082460" }, boxShadow: "none" } : { borderColor: NAVY, color: NAVY }) }}>
                Requests {requests.length > 0 && `(${requests.length})`}
              </Button>
            </Stack>
          )}
        </Stack>

        {error && !mentor && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "10px" }}>{error}</Alert>
        )}

        {/* Two-column layout */}
        {isMobile ? (
          // Mobile: show one panel at a time
          <Box sx={{ minHeight: "70vh" }}>
            {mobileView === "requests" ? <RequestsPanel /> : <ProfilePanel />}
          </Box>
        ) : (
          // Desktop: side by side
          <Stack direction="row" spacing={3} alignItems="stretch" sx={{ minHeight: "72vh" }}>
            <Box sx={{ width: 360, flexShrink: 0 }}>
              <RequestsPanel />
            </Box>
            <Box flex={1} minWidth={0}>
              <ProfilePanel />
            </Box>
          </Stack>
        )}
      </Box>

      <Footer />
    </Box>
  );
}